import React, { FC, memo } from "react";
import {
  AttributeConfigurationDc,
  AttributeType,
  StringSubType,
  GeometryType,
  LayerReferenceConfigurationDc,
} from "@evergis/api";
import { SrId } from "@evergis/react";
import { IconToggle, Input, Tooltip } from "@evergis/ui";
import { getFieldTypeLangKey } from "evergis/core/attributes/fieldTypes";
import { useTranslation } from "react-i18next";

import { AttributeFormat } from "../../../DialogComponents/Attributes/AttributeFormat/AttributeFormat";
import { HideLayerAttribute } from "../../../HideLayerAttribute/HideLayerAttribute";
import { Container, Description, Flex, NameContainer, AttributeName } from "./styled";

import { useToggle } from "../../../../../hooks/useToggle";
import { useLayerAttributeUpdate } from "../../hooks/useLayerAttributeUpdate";

import { UpdateAttribute } from "../../../../../hooks/attributes/useAttributes";

export type LayerAttribute = AttributeConfigurationDc & {
  id: string;
  type: AttributeType;
  subType?: StringSubType;
  isDisplayed?: boolean;
  classified?: boolean;
  resetClassification?: boolean;
  deleted?: boolean;
  hasIndex?: boolean;
  sysAttribute?: boolean;
  srid?: SrId;

  /**
   * set this property to true if
   * attribute should not been displayed
   * in UI
   */
  private?: boolean;
  tableName?: string;
  description?: string;
  referenceId?: string;
};

export interface AttributesProps {
  attribute: LayerAttribute;
  displayField: string;
  geometryType: GeometryType;
  updateAttribute: UpdateAttribute<LayerAttribute>;
  setDisplayField: (fieldName: string) => void;
  firstVisibleAttribute?: LayerAttribute;
  onDelete?: (id: string) => void;
  onChange?: UpdateAttribute<LayerAttribute>;
  deleteDisabled?: boolean;
  layerReferences?: LayerReferenceConfigurationDc[];
}

export const LayerAttributeItem: FC<AttributesProps> = memo(
  ({
    attribute,
    displayField,
    geometryType,
    updateAttribute,
    setDisplayField,
    firstVisibleAttribute,
    layerReferences,
  }) => {
    const { t } = useTranslation();
    const { attributeName, isDisplayed, type, alias, subType, id, stringFormat, expression, description } = attribute;
    const [openHideAttributeDialog, toggleHideAttributeDialog] = useToggle(false);

    const { toggleDisplayed, updateAlias, hideWithClassification } = useLayerAttributeUpdate(
      displayField,
      attribute,
      setDisplayField,
      updateAttribute,
      firstVisibleAttribute,
      toggleHideAttributeDialog,
    );

    const attributeNameLengthLimit = attributeName.length * 8 > 120;

    return (
      <Container key={`attr_${attributeName}`}>
        <Flex>
          <IconToggle
            onClick={toggleDisplayed}
            isSelected={isDisplayed}
            kind={isDisplayed ? "password_show" : "password_hide"}
            title={
              isDisplayed
                ? t("editResource.attributeOff", { ns: "dialogs" })
                : t("editResource.attributeOn", { ns: "dialogs" })
            }
          />
          <IconToggle
            kind={"displayfield"}
            title={displayField !== attributeName ? t("editResource.setAsCardTitle", { ns: "dialogs" }) : null}
            isSelected={displayField === attributeName && isDisplayed}
            onClick={() => setDisplayField(attributeName)}
            disabled={!isDisplayed}
          />
          <NameContainer>
            {attributeNameLengthLimit ? (
              <Tooltip distance={8} flip={false} indent={4} arrow placement={"right"} content={attributeName}>
                {ref => <AttributeName ref={ref}>{`${attributeName}`}</AttributeName>}
              </Tooltip>
            ) : (
              <AttributeName>{`${attributeName}`}</AttributeName>
            )}

            <Description>{t(getFieldTypeLangKey(type, subType), { ns: "common" })}</Description>
          </NameContainer>
        </Flex>
        <Flex style={{ alignItems: "center" }}>
          <Description style={{ marginLeft: "1rem" }}>
            {t("editResource.attributeAlias", { ns: "dialogs" })}:
          </Description>
          <Input width={"14rem"} disabled={!isDisplayed} value={alias} onChange={updateAlias} />
          <AttributeFormat
            type={type}
            subType={subType}
            geometryType={geometryType}
            id={id}
            updateAttribute={updateAttribute}
            stringFormat={stringFormat}
            isCalculated={!!expression}
            description={description}
            attributeConfiguration={attribute}
            layerReferences={layerReferences}
          />
        </Flex>
        {openHideAttributeDialog && (
          <HideLayerAttribute
            attributeName={attributeName}
            onApply={hideWithClassification}
            onCancel={toggleHideAttributeDialog}
          />
        )}
      </Container>
    );
  },
);
